/**
 * "How is engagement score calculated?" dialog for the Track tab.
 * Mirrors the backend model in proposal_view/funnel.py compute_score.
 */
import { HelpCircle, Flame, Sparkles, Snowflake, MoonStar, AlertTriangle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "../ui/dialog";

const SIGNALS = [
  { label: "Recency", max: 25, how: "Full 25 pts if opened in the last 24h, fading to 0 by day 14. The biggest lever, so stale interest sinks on its own." },
  { label: "Time on Costs", max: 20, how: "Scales with cumulative minutes on the Costs tab. Full marks at 5+ minutes." },
  { label: "Sample / scope edits", max: 20, how: "7 pts per sample-size or unit adjustment. Modelling budgets is strong intent." },
  { label: "Currency switches", max: 10, how: "5 pts each. Evaluating the quote in their own currency." },
  { label: "Downloads", max: 15, how: "8 pts per Excel pull, counted once per real download." },
  { label: "Forward bonus", max: 10, how: "Flat +10 if they forwarded the link to any colleague." },
  { label: "Momentum", max: 10, how: "2 pts per repeat visit (max 6) plus 4 pts when a second person from the client opens it." },
];

const BANDS = [
  { icon: Flame, cls: "bg-rose-100 text-rose-700 border-rose-200", label: "Hot", rule: "score 65+" },
  { icon: Sparkles, cls: "bg-amber-100 text-amber-700 border-amber-200", label: "Warm", rule: "30 to 64" },
  { icon: Snowflake, cls: "bg-sky-100 text-sky-700 border-sky-200", label: "Cold", rule: "below 30" },
  { icon: MoonStar, cls: "bg-stone-100 text-stone-500 border-stone-200", label: "Not opened", rule: "no client open yet" },
];

export default function ScoreExplainer() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid="score-explainer-link"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1B4F8A] hover:text-[#3EA8DC] transition-colors"
        >
          <HelpCircle className="w-3.5 h-3.5" />
          How is the engagement score calculated?
        </button>
      </DialogTrigger>
      <DialogContent
        data-testid="score-explainer-dialog"
        className="max-w-xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-[#1B4F8A]">The engagement score, explained</DialogTitle>
          <DialogDescription className="text-xs">
            Each shared proposal is scored 0-100 from client behaviour only.
            Medicys staff previews never count.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 text-sm">
          <div>
            <h4 className="text-[11px] font-mono uppercase tracking-[0.14em] text-stone-400 mb-2">
              The 7 ingredients
            </h4>
            <div className="divide-y divide-stone-100 border border-stone-200 rounded-lg overflow-hidden">
              {SIGNALS.map((s) => (
                <div key={s.label} className="flex items-start gap-3 px-3 py-2 bg-white">
                  <span className="shrink-0 w-14 text-right font-mono text-[11px] font-semibold text-emerald-600 pt-0.5">
                    up to {s.max}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[13px] font-medium text-slate-900">{s.label}</span>
                    <span className="block text-[11px] text-stone-500 leading-snug">{s.how}</span>
                  </span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-stone-500 mt-1.5">
              Points sum and cap at 100.
            </p>
          </div>

          <div>
            <h4 className="text-[11px] font-mono uppercase tracking-[0.14em] text-stone-400 mb-2">
              Temperature bands
            </h4>
            <div className="flex flex-wrap gap-2">
              {BANDS.map((b) => {
                const Icon = b.icon;
                return (
                  <span key={b.label} className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${b.cls}`}>
                    <Icon className="w-3 h-3" />
                    {b.label}
                    <span className="font-normal opacity-75">· {b.rule}</span>
                  </span>
                );
              })}
            </div>
          </div>

          <div className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2.5">
            <p className="text-[12px] text-orange-800 leading-snug">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1 align-[-2px]" />
              <strong>Going cold:</strong> a proposal that earned 25+ intent
              points (dwell, edits, downloads, FX or forwards) but has been
              silent for 5+ days gets the amber flag. That is your
              &ldquo;call them this week&rdquo; list.
            </p>
          </div>

          <p className="text-[11px] text-stone-500 leading-relaxed">
            The <span className="font-mono text-emerald-600">+N</span> lines on
            each card are the top 3 scoring reasons, so you can see at a glance
            why something is hot. In a bundle, every proposal number scores
            independently, showing which option the client is really spending
            time on.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
